import React, { useState, useEffect, useCallback, ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { IVehicle, IDocument } from "dataModels/contract";
import { getVehicle, downloadInfo, downloadDocument, deleteEntity, changeStatus, putDocument } from "net/contracts";
import { Container, Row, Col } from "react-grid-system";
import { BadgeStatus, RoutePath, EntityType, DATE_FORMAT, DocumentType, VehicleType, UICommand, IAppUserProfile, DsLayout, ResponseStatus, ResponseCode, ULTRA_LATE_DATE } from "shared/constants";
import { Textlink, Button, Table } from "@ds/ds-snam/react";
import vehicleIcon from "shared/assets/icons/Automezzi.svg";
import "views/contractView/styles/driverDataStyles.css";//it's the same style
import VehicleDataInfo from "./vehicleDataInfo";
import { saveAs } from 'file-saver';
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import ConfirmPopUp from "components/ConfirmPopUp";
import moment from "moment";
import SimpleTable, { SimpleTableType } from "components/SimpleTable";
import ApproveButtons from "components/ApproveButtons";
import DialogMessage from "components/DialogMessage";
import { v4 as uuid } from "uuid";
import _ from "lodash";
import DsBadge from "components/DsBadge";
import useAppUser from "hooks/useAppUser";
import { RootState } from "state/appStore";
import DsHorizontalDivider from "components/DsHorizontalDivider";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import { ITableCell, ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { getDSTableSharedElements, getTableCellsHeaderSimple } from "shared/utils/dsComponentUtils";
import { documentTableLegend } from "../shared/listUtils";
export const fileCategories = {
    documents: [
        { type: DocumentType.REGISTRATION_DOCUMENT, headerPosition: 0 },
        { type: DocumentType.TECHNICAL_CHECK, headerPosition: 1 },
        { type: DocumentType.PID, headerPosition: 2 },
        { type: DocumentType.GA_DRAWING, headerPosition: 3 },
    ],
    procedures: [
        { type: DocumentType.PROCEDURES, headerPosition: 4 }
    ],
    certifications: [
        { type: DocumentType.INSURANCE_CERTIFICATE, headerPosition: 5 },
        { type: DocumentType.ADR_DECLARATION, headerPosition: 6 },
        { type: DocumentType.TEST_CERTIFICATE, headerPosition: 7 },
        { type: DocumentType.FLAME_ARREST, headerPosition: 8 }
    ]
}
export const getTranslationPath = (status: BadgeStatus): string => {
    switch (status) {
        case BadgeStatus.APPROVED:
            return "approve";
        case BadgeStatus.CREDITED:
            return "credit";
        case BadgeStatus.ACCREDITED_SALES:
            return "credit";
        case BadgeStatus.NOTAPPROVED:
            return "reject";
        case BadgeStatus.NOT_CREDITED:
            return "rejectCredit";
        default:
            return "";
    }
};
const VehicleData: React.FC = (): JSX.Element => {
    const { id } = useParams();
    const [vehicleData, setVehicleData] = useState<IVehicle>();
    const [command, setCommand] = useState<UICommand | undefined>();
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const appUserProfile:IAppUserProfile | undefined = useAppUser()?.userProfile;

    const { t } = useTranslation(['contract', 'app']);
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const loadVehicleData = useCallback(() => {
        dispatch(requestPending());
        getVehicle(id!)
            .then((response) => {
                //console.log(response)
                setVehicleData(response.data)
            })
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [id, dispatch]);

    useEffect(() => {
        if (id)
            loadVehicleData();
    }, [id, loadVehicleData]);

    const existExpiredDocument = () => { 
        const today = new Date();
        return vehicleData!.documents.some(doc => 
            doc.expire !== null && new Date(doc.expire * 1000) < today
        );
    }

    const getVehicleStatusBadge = () => {

        let notificationStatus: string = "";
        let statusMessage: string = "";

        if (vehicleData?.statusComplete === BadgeStatus.APPROVED) {
            notificationStatus = "confirm";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.1');
        } else if (vehicleData?.statusComplete === BadgeStatus.TOAPPROVE) {
            notificationStatus = "warning";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.0')+t('vehicle.vehicleData.statusBadgeMessage.5'); //<--------
        } else if (vehicleData?.statusComplete === BadgeStatus.TO_CREDIT){
            notificationStatus = "warning";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.0')+t('vehicle.vehicleData.statusBadgeMessage.4'); //<--------
        }else if (vehicleData?.statusComplete === BadgeStatus.ACCREDITED_SALES){
            notificationStatus = "warning";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.0')+t('vehicle.vehicleData.statusBadgeMessage.4'); //<--------
        }else if (vehicleData?.statusComplete === BadgeStatus.NOTAPPROVED) {
            notificationStatus = "error";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.2');
        } else if (vehicleData?.statusComplete === BadgeStatus.NOT_CREDITED) {
            notificationStatus = "error";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.3');
        }else if ( (vehicleData?.statusComplete === BadgeStatus.EXPIRED) && (existExpiredDocument()===true)) {
            notificationStatus = "error";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.6');
        }else if ( (vehicleData?.statusComplete === BadgeStatus.EXPIRED) && (existExpiredDocument()===false)) {
            notificationStatus = "error";
            statusMessage = t('vehicle.vehicleData.statusBadgeMessage.7')
        }

        return <div style={{ display: "flex", width: "230px", justifyContent: "flex-end", alignItems: "center", gap: "8px",marginTop:"46px" }}><ds-notification-badge
            notification-status={notificationStatus}
            badge-variant="default"
            badge-number="0"
            badge-aria-label="Badge aria label"
            is-small={true}
            background-color=""
        ></ds-notification-badge>
            <span className="statusMessage">
                {statusMessage}
            </span></div>

    }

    const downloadVehicleInfo = () => {
        downloadInfo(id!, EntityType.VEHICLE)
            .then((response) => {
                let file = new File([response.data], "vehicle_" + id! + ".pdf");
                saveAs(file);
            })
            .catch((error) => {
                console.log(error)
            });
    }

    const downloadVehicleDocument = (documentType: DocumentType) => {
        downloadDocument(id!, EntityType.VEHICLE, documentType)
            .then(response => {
                if(response.data.type === 'application/force-download'){
                    let file = new File([response.data], "vehicle_" + documentType + "_" + id! + ".pdf");
                    saveAs(file);
                }else{
                    let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: 'Warning',
                        description: "Blob non trovato.",
                        status: 'warning',
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    dispatch(showNotification(popUpAction));
                }
                
            })
            .catch(error => {
                let popUpAction: INotificationAction;
                popUpAction = {
                    show: true,
                    title: 'Error',
                    description: error.message,
                    status: 'error',
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                console.log(error);
            })
    }

    const getBadgeStatus = (status: BadgeStatus) => {
        let notificationStatus = "";

        if (status === BadgeStatus.EXPIRED) {
            notificationStatus = 'error'
        } else if (status === BadgeStatus.TO_CREDIT) {
            notificationStatus = 'warning'
        } else if (status === BadgeStatus.VALID) {
            notificationStatus = 'confirm'
        }

        return notificationStatus;
    }


    const getDivider = () => {
        return  <DsHorizontalDivider/>
    }
    const tableCols = (() => {
        return t("vehicle.vehicleData.fileTable.tableColumns", { ns: 'contract', returnObjects: true }) as string[];
    })();

    const tableRows = (category: string, tablePosition: number) => {
        
        const files: { document: IDocument, label: string }[] = [];
        const selectedCategory = fileCategories[category as keyof typeof fileCategories];
        //console.log(selectedCategory)
        selectedCategory.forEach((category) => {
            let foundDocument = _.find(vehicleData?.documents, { 'type': category?.type });
            //console.log("- search :" + category?.type, " result:", foundDocument);
            if (foundDocument) {
                files.push(
                    {
                        document: foundDocument,
                        label: t('vehicle.vehicleForm.sections.fileInput.title.' + category?.headerPosition)
                    }
                );
            }
        });

        return files.map((fileObj: { document: IDocument, label: string }) => {
            let document = fileObj.document;
            return (
                <tr key={uuid()}>
                    <td>
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <DsBadge notificationStatus={getBadgeStatus(document.status!)} />
                            {fileObj.label}
                        </span>
                    </td>

                    <td>
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {document.expire ? parseDateForDS(moment.unix(document.expire).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1) : ""}
                        </span>
                    </td>

                    <td style={{ textAlign: "center" }}>
                        <Textlink
                            textlinkText={t('general.buttons.modify', { ns: 'app' })}//"Modifica"
                            textlinkTextSize="SMALL"
                            isRouterLink={true}
                            onClickLink={() => {
                                navigate(RoutePath.CONTRACTS_EDIT_DOCUMENT + "?entityId=" + id + "&entityType=" + EntityType.VEHICLE + "&documentType=" + document.type);
                            }}
                            hidden={!appUserProfile?.editEntityPolicy.includes(EntityType.VEHICLE)}
                        />
                    </td>
                    <td>
                        <Button
                            buttonType="button"
                            buttonUIVariant="tertiary"
                            buttonSize="medium"
                            buttonLabel=""
                            buttonIcon="icon-download"
                            isDisabled={false}
                            buttonAriaLabel="Aria text here"
                            onClickEvent={(event: Event) => {
                                downloadVehicleDocument(document.type as DocumentType);
                            }}
                        ></Button>
                    </td>

                </tr>
            )
        });
    }

    const deleteVehicle = useCallback(() => {
        const popupAction: INotificationAction = {
            show: true,
            title: t(`vehicle.vehicleData.successToast.title`),//"automezzo eliminato correttamente",
            description: t(`vehicle.vehicleData.successToast.description`),//"Il profilo dell'automezzo è eliminato correttamente in piattaforma",//t(`customer.customerForm.successToast.${translationField}.subTitle`),
            status: "confirm",
            onClose: (event: Event) => {
                dispatch(hideNotification());
            }
        };

        dispatch(requestPending());
        deleteEntity(id!, EntityType.VEHICLE)
            .then((response) => {
                dispatch(showNotification(popupAction));
                navigate(RoutePath.CONTRACTS_VEHICLE_INFO);
            })
            .catch((error) => {
                console.log(error);
                let popUpAction:INotificationAction = {
                    show: true,
                    title: "Error",
                    description: 
                    (
                        error.response.data.message.includes("FK_Reservations_LastTankerTruckId_LastLoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservation_TankerTruckId_LoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservations_LastIsoContainerId_LastLoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservation_IsoContainerId_LoadingSideTypeId") ||
                        error.response.data.message.includes("CK_Reservations_LoadingSideAssignmentTimestampNullability")
                    )
                    ?"Impossibile eliminare veicolo coinvolto in prenotazioni attive":error.message,
                    status: "error",
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }, [id, dispatch, navigate, t]);

    const changeEntityStatus = (status: BadgeStatus, message?: string) => {

        
        const translationPath = getTranslationPath(status);
        if (vehicleData) {
            changeStatus(vehicleData?.vehicleId, EntityType.VEHICLE, status, (message ? message : ''))
                .then((response) => {
                    let popUpAction: INotificationAction;
                    const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t("vehicle.vehicleData.approvalSuccessToast." + translationPath + ".title") );
                    const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t("vehicle.vehicleData.approvalSuccessToast." + translationPath + ".description");
                    const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                    popUpAction = {
                        show: true,
                        title: popUpTitle,//'Veicolo approvato',
                        description: popUpDescription,//"Il Veicolo è stato approvato correttamente in piattaforma",
                        status: popUpStatus,
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    setCommand(undefined);
                    dispatch(showNotification(popUpAction));
                    loadVehicleData();

                    /**
                     Se è pianale o trattore deve fare in automatico l'approvazione dopo l'ultimo accreditamento (BadgeStatus.CREDITED)
                     */
                    if(response.data.status!==ResponseStatus.ERROR && response.data.status!==ResponseCode.UNPROCESSABLE_CONTENT && status===BadgeStatus.CREDITED && (vehicleData?.type === VehicleType.TRUCK || vehicleData?.type === VehicleType.FLOOR)){
                        changeStatus(vehicleData?.vehicleId, EntityType.VEHICLE, BadgeStatus.APPROVED, (message ? message : ''))
                            .then((response) => {
                                let popUpAction: INotificationAction;
                                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t("vehicle.vehicleData.approvalSuccessToast." + getTranslationPath(BadgeStatus.APPROVED) + ".title") );
                                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t("vehicle.vehicleData.approvalSuccessToast." + getTranslationPath(BadgeStatus.APPROVED) + ".description");
                                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");   
                                popUpAction = {
                                    show: true,
                                    title: popUpTitle,//'Veicolo approvato',
                                    description: popUpDescription,//"Il Veicolo è stato approvato correttamente in piattaforma",
                                    status: popUpStatus,
                                    onClose: (event: Event) => {
                                        dispatch(hideNotification());
                                    }
                                };
                                setCommand(undefined);
                                dispatch(showNotification(popUpAction));
                                loadVehicleData();
                            })
                            .catch(error => {
                                console.log(error);
                            })
                            .finally(() => {
                                dispatch(requestPristine());
                            })
                    }

                    /** se sto facendo il primo accreditamento, faccio in automatico anche il secondo */
                    if(status===BadgeStatus.ACCREDITED_SALES){
                        changeStatus(vehicleData?.vehicleId, EntityType.VEHICLE, BadgeStatus.CREDITED, (message ? message : ''))
                            .then((response) => {
                                let popUpAction: INotificationAction;
                                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t("vehicle.vehicleData.approvalSuccessToast." + translationPath + ".title") );
                                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t("vehicle.vehicleData.approvalSuccessToast." + translationPath + ".description");
                                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                                popUpAction = {
                                    show: true,
                                    title: popUpTitle,//'Veicolo approvato',
                                    description: popUpDescription,//"Il Veicolo è stato approvato correttamente in piattaforma",
                                    status: popUpStatus,
                                    onClose: (event: Event) => {
                                        dispatch(hideNotification());
                                    }
                                };
                                setCommand(undefined);
                                dispatch(showNotification(popUpAction));
                                loadVehicleData();
                            })
                            .catch(error => {
                                console.log(error);
                            })
                            .finally(() => {
                                dispatch(requestPristine());
                            })
                    }

                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    dispatch(requestPristine());
                })
        }
    }


    //da qui
    const getTableRows = (categoryKey: keyof typeof fileCategories): ITableRow[] => {
        let myRetArr: ITableRow[] = [];
        const files: { document: IDocument, label: string }[] = [];
        const selectedCategory = fileCategories[categoryKey];
    
        selectedCategory.forEach((category) => {
            let foundDocument = _.find(vehicleData?.documents, { 'type': category?.type });
            if (foundDocument) {
                files.push({
                    document: foundDocument,
                    label: t('vehicle.vehicleForm.sections.fileInput.title.' + category?.headerPosition)
                });
            }
        });
    
        files.forEach(file => {
            let tempObj: ITableRow = {
                tableCells: [
                    { 
                        type: "text-badge", 
                        text: file.label,
                        badgeStatus: file.document.name===DocumentType.EMPTY_FILE?'warning':getBadgeStatus(file.document.status as BadgeStatus) as "confirm" | "error" | "warning"
                    },
                    { 
                        type: "text", 
                        text: file.document.expire && moment.unix(file.document.expire as number).toDate().getFullYear()!==moment.unix(ULTRA_LATE_DATE as number).toDate().getFullYear()
                            ? parseDateForDS(moment.unix(file.document.expire as number).format(DATE_FORMAT), IParseDateFormatType.DATE_FORMAT1) 
                            : ""
                    },
                    { 
                        type: "text", 
                        text: file.document.lastUpdate 
                    },
                    { 
                        type: "text", 
                        text: file.document.lastUpdateUser
                    },
                ],
                //rightIcon: "icon-download",
                rightIconMenu:true,
                "rightIconMenuData":[
                    {
                        "actionLabel": t("general.buttons.modify",{ ns: "app" }),
                        "actionAriaLabel": t("general.buttons.modify",{ ns: "app" }), //EDIT
                        "actionDisabled": !appUserProfile?.editEntityPolicy.includes(EntityType.VEHICLE),
                    },
                    {
                        "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                        "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                        "actionDisabled": false,
                    }
                ]
            };
            // ✅ Aggiungo la terza azione SOLO se rispetta le condizioni
            const extraAction = {
                actionLabel: t("general.buttons.delete_document", { ns: "app" }),
                actionAriaLabel: t("general.buttons.delete_document", { ns: "app" }),
                actionDisabled: false
            };

            const allowedTruckDocs = [
                DocumentType.GA_DRAWING,
                DocumentType.TEST_CERTIFICATE,
                DocumentType.FLAME_ARREST
            ];

            const allowedFloorDocs = [
                DocumentType.GA_DRAWING,
                DocumentType.TEST_CERTIFICATE
            ];

            if ( vehicleData &&
                ((vehicleData.type === VehicleType.TRUCK && allowedTruckDocs.includes(file.document.type as DocumentType)) ||
                (vehicleData.type === VehicleType.FLOOR && allowedFloorDocs.includes(file.document.type as DocumentType)))
            ) {
                tempObj.rightIconMenuData!.push(extraAction);
            }

            myRetArr.push(tempObj);
        });
    
        return myRetArr;
    };
    const getTableRowsCert = (): ITableRow[] => getTableRows("certifications");
    const getTableRowsProc = (): ITableRow[] => getTableRows("procedures");
    const getTableRowsDocs = (): ITableRow[] => getTableRows("documents");
    //a qui



    const myTableConfigCert: ITableConfig = {
        ...getDSTableSharedElements(),
        tableHeader: {
            
            tableCellsHeader: [
                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.0", { ns: 'contract'}) as string,"col0","25%","left"),
                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.1", { ns: 'contract'}) as string,"col1","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.2", { ns: 'contract' }) as string,"col2","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.3", { ns: 'contract' }) as string,"col3","25%","left")
                                
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRowsCert()
    };
    const myTableConfigDocs: ITableConfig = {
                        ...getDSTableSharedElements(),
                        tableHeader: {
                            
                            tableCellsHeader: [
                                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.0", { ns: 'contract'}) as string,"col0","25%","left"),
                                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.1", { ns: 'contract'}) as string,"col1","25%","left"),
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.2", { ns: 'contract' }) as string,"col2","25%","left"),
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.3", { ns: 'contract' }) as string,"col3","25%","left")
                                            
                            ],
                            hasSearchBox: true,
                        },
                        tableRows: getTableRowsDocs()
    };
    const myTableConfigProc: ITableConfig = {
        ...getDSTableSharedElements(),
        tableHeader: {
            
            tableCellsHeader: [
                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.0", { ns: 'contract'}) as string,"col0","25%","left"),
                getTableCellsHeaderSimple(t("vehicle.vehicleData.fileTable.tableColumns.1", { ns: 'contract'}) as string,"col1","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.2", { ns: 'contract' }) as string,"col2","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.3", { ns: 'contract' }) as string,"col3","25%","left")
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRowsProc()
};

const createKebabActionHandlerForEdit = (categoryKey: keyof typeof fileCategories) => {
    return (event: Event) => {
        const files: { document: IDocument; label: string }[] = [];
        const selectedCategory = fileCategories[categoryKey];

        selectedCategory.forEach((category) => {
            const foundDocument = _.find(vehicleData?.documents, { type: category?.type });
            if (foundDocument) {
                files.push({
                    document: foundDocument,
                    label: t('vehicle.vehicleForm.sections.fileInput.title.' + category?.headerPosition),
                });
            }
        });

        const selectedFile = files[(event as CustomEvent).detail.row];
        if (selectedFile) {
            navigate(
                `${RoutePath.CONTRACTS_EDIT_DOCUMENT}?entityId=${id}&entityType=${EntityType.VEHICLE}&documentType=${selectedFile.document.type}`
            );
        }
    };
}
const handleKebabActionForDownload = (categoryKey: keyof typeof fileCategories) => (event: Event) => {
    const files: { document: IDocument, label: string }[] = [];
    const selectedCategory = fileCategories[categoryKey];

    selectedCategory.forEach((category) => {
        const foundDocument = _.find(vehicleData?.documents, { type: category?.type });
        if (foundDocument) {
            files.push({
                document: foundDocument,
                label: t('vehicle.vehicleForm.sections.fileInput.title.' + category?.headerPosition)
            });
        }
    });

    downloadVehicleDocument(files[(event as CustomEvent).detail.row].document.type as DocumentType);
};

    const deleteSingleDocument = (entityId: string,entityType: EntityType,documentType: string | undefined,document: IDocument | undefined) => {
            let popUpAction: INotificationAction;
            dispatch(requestPending());
            const translationEntity = "drivers";
            putDocument(entityId!, entityType!, document!)
                .then(response => {
                    const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t(`${translationEntity}.documentEdit.successToast.title`));
                    const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`${translationEntity}.documentEdit.successToast.subTitle`);
                    const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                                    
                    popUpAction = {
                        show: true,
                        title: popUpTitle,
                        description: popUpDescription,
                        status: popUpStatus,
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    loadVehicleData();
                    dispatch(showNotification(popUpAction));
                }).catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    dispatch(requestPristine())
                });
                //eslint-disable-next-line
    }

    return (
    <div>

        <Row>
            <Col>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <p className="pageTitle" style={{ marginBottom:'16px'  }}>
                            {/*t('vehicle.vehicleList.vehicleType.' + vehicleData?.type)*/}
                            {t('vehicle.vehicleData.title')}
                        </p>
                        {/*{vehicleData?.status && vehicleData?.status === BadgeStatus.TOAPPROVE && <p className="pageSubTitle">
                            {t('vehicle.vehicleData.statusMessage.0')}
                        </p>}
                        {vehicleData?.status && vehicleData?.status === BadgeStatus.TO_CREDIT && <p className="pageSubTitle">
                            {t('vehicle.vehicleData.statusMessage.1')}
                        </p>}*/}
                    </div>
                    <div>
                        {getVehicleStatusBadge()}
                    </div>
                </div>
            </Col>
        </Row>

        {/** Delete and Credit buttons  */}
        <Row>
            <Col>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    {appUserProfile?.deleteEntityPolicy.includes(EntityType.VEHICLE)?
                        <Textlink
                        textlinkText={t('general.buttons.removeElement', { ns: 'app', what: t('vehicle.vehicleData.removeButton') })}//"Elimina automezzo"
                        textlinkTextSize="SMALL"
                        textlinkAriaLabel="aria-Textlink"
                        isRouterLink={true}
                        onClickLink={() => {
                            setCommand(UICommand.DELETE);
                        }}
                    />:<div />}

                    {/* Refuse/Credit buttons */}
                    {vehicleData && appUserProfile?.approveEntityPolicy.includes(EntityType.VEHICLE) &&
                    <div style={{ display: "flex", gap: "16px", marginTop:'12px' }}>
                        <ApproveButtons
                            currentStatus={vehicleData?.statusComplete}
                            entityType={EntityType.VEHICLE}
                            onClick={(command: UICommand) => {
                                switch (command) {
                                    case UICommand.APPROVE:
                                        changeEntityStatus(BadgeStatus.APPROVED);
                                        break;
                                    case UICommand.CREDIT:
                                        changeEntityStatus(BadgeStatus.CREDITED);
                                        break;
                                    case UICommand.CREDIT_SALES:
                                        changeEntityStatus(BadgeStatus.ACCREDITED_SALES);
                                        break;
                                    case UICommand.REJECT:
                                    case UICommand.REJECT_CREDIT:
                                        setCommand(command);
                                        break;
                                }
                            }}
                        />
                    </div>
                    }
                </div>
            </Col>
        </Row>

        {/**Avatar & infos*/}
        <Row>
            <Col>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px",marginTop:'24px' }}>
                        <div style={{ backgroundColor: "var(--interaction-active)", width: "64px", height: "64px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <img alt="" src={vehicleIcon} style={{ width: "32px", height: "32px" }} />
                        </div>
                        <div className="avatarNotes--driver">
                            <div>
                                <span>
                                    {t('vehicle.vehicleData.avatar.label1')}
                                </span>
                                <span>{vehicleData ? t(`vehicle.vehicleList.vehicleType.${vehicleData?.type}`) : "-"}</span>
                            </div>
                            <div>
                                <span>
                                    {/*Targa||Iso container Id*/}
                                    {t('vehicle.vehicleData.avatar.label2')}
                                </span>
                                <span>
                                    {vehicleData?.type === VehicleType.ISO_CONTAINER ? vehicleData.isoContainerCode : vehicleData?.licencePlate}
                                </span>
                            </div>
                        </div>
                    </div>
                <div className="driverDataContainer">

                    

                    {vehicleData &&
                        <VehicleDataInfo
                            vehicle={vehicleData}
                            excludeFields={["licencePlate", "isoContainerCode"]}
                            isData={true}
                        />}

                    

                </div>
                <div className="driverButtonContainer">
                        {appUserProfile?.editEntityPolicy.includes(EntityType.VEHICLE)&&
                            <Button
                                buttonType="button"
                                buttonUIVariant="secondary"
                                buttonSize="medium"
                                buttonLabel={t('general.buttons.edit', { ns: 'app' })}//'Modifica informazioni'
                                buttonIcon=""
                                isDisabled={false}
                                buttonAriaLabel="modify info button"
                                onClick={() => {
                                    navigate(RoutePath.CONTRACTS_EDIT_VEHICLE + "/" + id);
                                }}
                            ></Button>
                        }
                        <Button
                            buttonType="button"
                            buttonUIVariant="primary"
                            buttonSize="medium"
                            buttonLabel={t('general.buttons.download', { ns: 'app' })}//'Scarica informazioni'
                            buttonIcon=""
                            isDisabled={false}
                            buttonAriaLabel="download info button"
                            onClick={downloadVehicleInfo}
                        ></Button>
                    </div>
            </Col>
        </Row>

        {/** File tables*/}
        <Row>
            <Col>
                <div className="fileTableTitle">
                    {t('vehicle.vehicleData.fileTable.title.0')}
                    <div className="listTableContainer">
                        {/*<SimpleTable
                            id={"simpleTable_1"}
                            columns={tableCols}
                            rows={tableRows("documents", 0)}
                            tableType={SimpleTableType.DOCUMENTS}
                        />*/}
                        {
                            <Table 
                                tableConfig={myTableConfigDocs} 
                                onClickKebabAction1={createKebabActionHandlerForEdit("documents")}
                                onClickKebabAction2={handleKebabActionForDownload("documents")}
                                onClickKebabAction3={(event:Event)=>{ //elimina documento
                                    /**
                                    SE E' UN TRACTOR
                                        row 0 -> registration doc
                                        row 1 -> technical check
                                        row 2 -> GA DRAWING //facoltativa
                                    SE E' UN SEMITRAILER
                                        row 0 registration doc
                                        row 1 techincal check
                                        row 2 p&D
                                        row 3 GA Drawing
                                    SE E' UN ISO
                                        row 0 p&D
                                        row 1 GADrawing
                                    SE E' UN FLATBED (FLOOR)
                                        row 0 registration document
                                        row 1 techincal check
                                        row 2 GA Drawing //facoltativo
                                     */
                                    //console.log((event as CustomEvent).detail.row);
                                    //Mi serve cancellare documenti solo nel caso in cui sono in Tractor o Flatbed, perchè sno gli unici con docs facoltativi

                                    if( vehicleData?.type === VehicleType.TRUCK){
                                        switch((event as CustomEvent).detail.row){
                                            case 2:
                                                deleteSingleDocument(
                                                    id!,
                                                    EntityType.VEHICLE,
                                                    vehicleData?.documents[2].type, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                    {
                                                        "type": vehicleData?.documents[2].type as DocumentType, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                        "name": DocumentType.EMPTY_FILE,
                                                        "size": 0,
                                                        "content": "",
                                                        "expire": null,
                                                    }
                                                );
                                            break;
                                            default:
                                            break;
                                        }
                                    }else if( vehicleData?.type === VehicleType.FLOOR){
                                        switch((event as CustomEvent).detail.row){
                                            case 2:
                                                deleteSingleDocument(
                                                    id!,
                                                    EntityType.VEHICLE,
                                                    vehicleData?.documents[2].type, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                    {
                                                        "type": vehicleData?.documents[2].type as DocumentType, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                        "name": DocumentType.EMPTY_FILE,
                                                        "size": 0,
                                                        "content": "",
                                                        "expire": null,
                                                    }
                                                );
                                            break;
                                            default:
                                            break;
                                        }
                                    }  
                                }}
                            />
                        }
                        {documentTableLegend(t('vehicle.vehicleData.fileTable.tableLegend.0'),t('vehicle.vehicleData.fileTable.tableLegend.1'),t('transporter.transporterData.fileTable.tableLegend.3'))}
                    </div>
                </div>
            </Col>
        </Row>
        {getDivider()}
        {(vehicleData?.type === VehicleType.TANKER || vehicleData?.type === VehicleType.ISO_CONTAINER) && <Row>
            <Col>
                <div className="fileTableTitle">
                    {t('vehicle.vehicleData.fileTable.title.1')}
                    <div className="listTableContainer">
                        {/*<SimpleTable
                            id={"simpleTable_2"}
                            columns={tableCols}
                            rows={tableRows("procedures", 1)}
                            tableType={SimpleTableType.DOCUMENTS}
                        />*/}
                        {
                            <Table 
                                tableConfig={myTableConfigProc} 
                                onClickKebabAction1={createKebabActionHandlerForEdit("procedures")}
                                onClickKebabAction2={handleKebabActionForDownload("procedures")}
                                onClickKebabAction3={(event:Event)=>{ 
                                }}
                            />
                        }
                        {documentTableLegend(t('vehicle.vehicleData.fileTable.tableLegend.0'),t('vehicle.vehicleData.fileTable.tableLegend.1'),t('transporter.transporterData.fileTable.tableLegend.3'))}
                    </div>
                </div>
            </Col>
        </Row>}
        {getDivider()}
        <Row>
            <Col>
                <div className="fileTableTitle">
                    {t('vehicle.vehicleData.fileTable.title.2')}
                    <div className="listTableContainer">
                        {
                            <Table 
                                tableConfig={myTableConfigCert} 
                                onClickKebabAction1={createKebabActionHandlerForEdit("certifications")}
                                onClickKebabAction2={handleKebabActionForDownload("certifications")}
                                onClickKebabAction3={(event:Event)=>{ //elimina documento
                                    /**
                                    SE E' UN TRACTOR
                                        row 0 -> third part liability
                                        row 1 -> ADR certificate
                                        row 2 test certificate //facoltativa
                                        row 3 flame arrester //facoltativa
                                    SE E' UN SEMITRAILER
                                        row 0 insurance certificate
                                        row 1 ADR cert
                                        row 2 test Certificate
                                    SE E' UN ISO
                                        row 0 adr
                                        row 1 test certificate
                                    SE E' UN FLATBED (FLOOR)
                                        row 0 insurance certificate
                                        row 1 ADR
                                        row 2 test certificate //facoltativo
                                    */
                                    //console.log((event as CustomEvent).detail.row);
                                    if( vehicleData?.type === VehicleType.TRUCK){
                                        switch((event as CustomEvent).detail.row){
                                            case 2:
                                                deleteSingleDocument(
                                                    id!,
                                                    EntityType.VEHICLE,
                                                    vehicleData?.documents[5].type, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                    {
                                                        "type": vehicleData?.documents[5].type as DocumentType, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                        "name": DocumentType.EMPTY_FILE,
                                                        "size": 0,
                                                        "content": "",
                                                        "expire": ULTRA_LATE_DATE,
                                                    }
                                                );
                                            break;
                                            case 3:
                                                deleteSingleDocument(
                                                    id!,
                                                    EntityType.VEHICLE,
                                                    vehicleData?.documents[6].type, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                    {
                                                        "type": vehicleData?.documents[6].type as DocumentType, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                        "name": DocumentType.EMPTY_FILE,
                                                        "size": 0,
                                                        "content": "",
                                                        "expire": null,
                                                    }
                                                );
                                            break;
                                            default:
                                            break;
                                        }
                                    }else if( vehicleData?.type === VehicleType.FLOOR){
                                        switch((event as CustomEvent).detail.row){
                                            case 2:
                                                deleteSingleDocument(
                                                    id!,
                                                    EntityType.VEHICLE,
                                                    vehicleData?.documents[5].type, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                    {
                                                        "type": vehicleData?.documents[5].type as DocumentType, // qui bisogna mettere il relativo indice nell'oggetto di get-vehicle
                                                        "name": DocumentType.EMPTY_FILE,
                                                        "size": 0,
                                                        "content": "",
                                                        "expire": ULTRA_LATE_DATE,
                                                    }
                                                );
                                            break;
                                            default:
                                            break;
                                        }
                                    } 
                                }}
                            />
                        }
                        {documentTableLegend(t('vehicle.vehicleData.fileTable.tableLegend.0'),t('vehicle.vehicleData.fileTable.tableLegend.1'),t('transporter.transporterData.fileTable.tableLegend.3'))}
                    </div>
                </div>
            </Col>
        </Row>

        {/** Delete confirm */}
        {command && command === UICommand.DELETE &&
            <ConfirmPopUp
                title={t('vehicle.vehicleData.deleteConfirm.title')}//"Sei sicuro di voler eliminare l'autista?"
                description={t('vehicle.vehicleData.deleteConfirm.description')}//"L’azione è irreversibile e dovrai inserire ex novo tutte le informazioni sull'autista."
                onClose={() => {
                    setCommand(undefined);
                }}
                onConfirm={() => {
                    setCommand(undefined);
                    deleteVehicle();
                }} />
        }
        {/** Reject || Reject-credit dialog */}
        {command && (command === UICommand.REJECT || command === UICommand.REJECT_CREDIT) &&
            <DialogMessage
                title={t((command === UICommand.REJECT ? 'vehicle.vehicleData.rejectDialog.reject' : 'vehicle.vehicleData.rejectDialog.rejectCredit') + ".title")}
                subtitle={t((command === UICommand.REJECT ? 'vehicle.vehicleData.rejectDialog.reject' : 'vehicle.vehicleData.rejectDialog.rejectCredit') + ".subTitle")}
                textareaLabel={t('drivers.driverData.rejectDialog.textareaLabel')}
                textAreaPlaceholder={t('drivers.driverData.rejectDialog.txtAreaPlaceholder')}
                textareaDescription={t('drivers.driverData.rejectDialog.textareaDescription')}
                submitButtonLabel={t('drivers.driverData.rejectDialog.submitButtonLabel')}
                icon={''}
                type={EntityType.DRIVER}
                onClose={() => setCommand(undefined)}
                onClick={(message: string) => {
                    if (vehicleData?.vehicleId) {
                        changeEntityStatus(
                            command === UICommand.REJECT ? BadgeStatus.NOTAPPROVED : BadgeStatus.NOT_CREDITED,
                            message
                        )
                    }
                }}
            />
        }


    </div>
    )
}

export default VehicleData;